import { useState, useEffect } from "react";
import axios from "axios";
import { BarChart3, Eye, Users, Mail, FileText, TrendingUp, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalyticsDashboard = ({ headers }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await axios.get(`${API}/admin/analytics`, { headers });
        setData(res.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchAnalytics();
  }, []);

  if (loading) return <div className="text-center py-12 text-slate-400">Chargement des analytics...</div>;
  if (!data) return <div className="text-center py-12 text-red-400">Erreur de chargement</div>;

  const maxDaily = Math.max(...(data.daily_views || []).map(d => d.count), 1);

  return (
    <div data-testid="analytics-dashboard">
      <h1 className="text-2xl font-bold mb-6">Analytics</h1>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="p-5 rounded-xl bg-[#131B2F] border border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-[#2395E3]/20"><Eye className="w-5 h-5 text-[#2395E3]" /></div>
            <span className="text-xs text-slate-400">Aujourd'hui</span>
          </div>
          <div className="text-3xl font-bold text-[#2395E3]">{data.views_today}</div>
          <div className="text-xs text-slate-500 mt-1">pages vues</div>
        </div>
        <div className="p-5 rounded-xl bg-[#131B2F] border border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-[#00CCC2]/20"><TrendingUp className="w-5 h-5 text-[#00CCC2]" /></div>
            <span className="text-xs text-slate-400">7 jours</span>
          </div>
          <div className="text-3xl font-bold text-[#00CCC2]">{data.views_week}</div>
          <div className="text-xs text-slate-500 mt-1">pages vues</div>
        </div>
        <div className="p-5 rounded-xl bg-[#131B2F] border border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-purple-500/20"><Calendar className="w-5 h-5 text-purple-400" /></div>
            <span className="text-xs text-slate-400">30 jours</span>
          </div>
          <div className="text-3xl font-bold text-purple-400">{data.views_month}</div>
          <div className="text-xs text-slate-500 mt-1">pages vues</div>
        </div>
        <div className="p-5 rounded-xl bg-[#131B2F] border border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-amber-500/20"><BarChart3 className="w-5 h-5 text-amber-400" /></div>
            <span className="text-xs text-slate-400">Total</span>
          </div>
          <div className="text-3xl font-bold text-amber-400">{data.total_views}</div>
          <div className="text-xs text-slate-500 mt-1">pages vues</div>
        </div>
      </div>

      {/* Content Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="p-4 rounded-xl bg-[#131B2F] border border-white/10 flex items-center gap-4">
          <div className="p-2 rounded-lg bg-green-500/20"><Mail className="w-5 h-5 text-green-400" /></div>
          <div>
            <div className="text-xl font-bold">{data.contacts_count}</div>
            <div className="text-xs text-slate-400">Messages</div>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-[#131B2F] border border-white/10 flex items-center gap-4">
          <div className="p-2 rounded-lg bg-blue-500/20"><FileText className="w-5 h-5 text-blue-400" /></div>
          <div>
            <div className="text-xl font-bold">{data.demo_count}</div>
            <div className="text-xs text-slate-400">Demandes demo</div>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-[#131B2F] border border-white/10 flex items-center gap-4">
          <div className="p-2 rounded-lg bg-pink-500/20"><Mail className="w-5 h-5 text-pink-400" /></div>
          <div>
            <div className="text-xl font-bold">{data.newsletter_count}</div>
            <div className="text-xs text-slate-400">Newsletter</div>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-[#131B2F] border border-white/10 flex items-center gap-4">
          <div className="p-2 rounded-lg bg-[#00CCC2]/20"><Users className="w-5 h-5 text-[#00CCC2]" /></div>
          <div>
            <div className="text-xl font-bold">{data.clients_count}</div>
            <div className="text-xs text-slate-400">Clients</div>
          </div>
        </div>
      </div>

      {/* Daily Chart */}
      <div className="p-6 rounded-xl bg-[#131B2F] border border-white/10 mb-8">
        <h3 className="text-lg font-semibold mb-4">Pages vues (30 derniers jours)</h3>
        {data.daily_views && data.daily_views.length > 0 ? (
          <div className="flex items-end gap-1 h-40" data-testid="daily-chart">
            {data.daily_views.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                <div className="absolute -top-8 bg-[#0B1121] px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  {d.date}: {d.count} vues
                </div>
                <div className="w-full rounded-t bg-gradient-to-t from-[#2395E3] to-[#00CCC2] transition-all hover:opacity-80"
                  style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: "2px" }} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-8">Pas encore de données</p>
        )}
      </div>

      {/* Top Pages */}
      <div className="p-6 rounded-xl bg-[#131B2F] border border-white/10">
        <h3 className="text-lg font-semibold mb-4">Top pages visitées</h3>
        {data.top_pages && data.top_pages.length > 0 ? (
          <div className="space-y-3">
            {data.top_pages.map((page, i) => {
              const pct = data.total_views > 0 ? (page.count / data.total_views) * 100 : 0;
              return (
                <div key={i} className="flex items-center gap-4" data-testid={`top-page-${i}`}>
                  <span className="text-sm text-slate-400 w-8 text-right">{i + 1}.</span>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium truncate">{page.path}</span>
                      <span className="text-sm text-slate-400">{page.count} ({pct.toFixed(1)}%)</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[#0B1121] overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#2395E3] to-[#00CCC2]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-8">Pas encore de données</p>
        )}
      </div>

      {/* GA Integration note */}
      <div className="mt-6 p-4 rounded-xl bg-[#0B1121] border border-white/10 text-sm text-slate-400">
        <p className="font-medium text-[#2395E3] mb-1">Google Analytics</p>
        <p>Configurez votre ID Google Analytics (ex: G-XXXXXXXXXX) dans l'onglet "Coordonnées" pour activer le tracking avancé. Le tracking n'est activé qu'après acceptation des cookies par le visiteur.</p>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
